import React from "react";
import { ReferredUser } from "types/affiliation.types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils";
import { LanguageNameSpaces } from "@shared/types";
import { formatDate } from "@shared/utils/date";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { checkSearch } from "@shared/utils/misc";
import { copyCodeToClipboard, openMessage } from "utils/misc";
import useReferredUsers from "features/affiliation/common/hooks/useReferredUsers";
import {
  AffiliationTabs,
  ExistingUsersStatistics,
  ReferredUsersStatistics,
} from "features/affiliation/common/types";
import { USERS_TAB_TO_REFERRAL_TYPE } from "features/affiliation/common/constants";
import {
  copyCodeOrLinkToClipboard,
  getReferralOrPromoCode,
} from "features/affiliation/common/utils";
import ReferralEmptyState from "features/affiliation/common/ReferralEmptyState";
import UsersTable from "features/affiliation/Users/UsersTable";
import TabLayout from "features/affiliation/NewLook/TabLayout";
import TabSummary, {
  SummaryItem,
} from "features/affiliation/NewLook/TabSummary";
import TablePanel from "features/affiliation/NewLook/TablePanel";
import RowList, {
  Line,
  RowFigure,
  RowMeta,
  RowSubFigure,
} from "features/affiliation/NewLook/RowList";
import ReferredUsersPanel from "features/affiliation/NewLook/ReferredUsersPanel";
import { useTierSummary } from "features/affiliation/NewLook/useTierSummary";
import { CopyValue } from "features/affiliation/NewLook/parts";
import { ChangeMeta } from "features/affiliation/NewLook/roles";
import { formatUsd, toNumber } from "features/affiliation/NewLook/newLookUtils";

interface NewLookUsersProps {
  tab: AffiliationTabs.REFERRED_USERS | AffiliationTabs.EXISTING_USERS;
}

/** The history rows carry `customer_id` (the type spells it `costumer_id`). */
type ReferredUserRow = ReferredUser & { customer_id: number };

/**
 * The referred users / existing users tabs in the new look: the user count
 * leads, then what they earned you (and, for referrals, your tier), the
 * new-users chart and the users.
 */
const NewLookUsers = ({ tab }: NewLookUsersProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const {
    referralHistoryQuery,
    users,
    tableTitle,
    getStatisticTotalValue,
    getStatisticLastMonthValue,
  } = useReferredUsers(tab);
  const tier = useTierSummary();
  const isReferredUsersTab = tab === AffiliationTabs.REFERRED_USERS;
  const loading = referralHistoryQuery.isLoading;
  const countStatistic = isReferredUsersTab
    ? ReferredUsersStatistics.REGISTERED_USERS
    : ExistingUsersStatistics.EXISTING_USERS;
  const earnedStatistic = isReferredUsersTab
    ? ReferredUsersStatistics.TOTAL_EARNED
    : ExistingUsersStatistics.TOTAL_EARNED;
  const fromLastMonth = t("common.from_last_month");

  const count = toNumber(getStatisticTotalValue(countStatistic));
  const countLastMonth = toNumber(getStatisticLastMonthValue(countStatistic));
  const earned = toNumber(getStatisticTotalValue(earnedStatistic));
  const earnedLastMonth = toNumber(getStatisticLastMonthValue(earnedStatistic));

  const items: SummaryItem[] = [
    {
      key: "earned",
      label: t(earnedStatistic),
      value: formatUsd(earned),
      meta: !loading && (
        <ChangeMeta
          value={earnedLastMonth}
          formatted={`+${formatUsd(earnedLastMonth)}`}
          suffix={fromLastMonth}
        />
      ),
    },
    ...(isReferredUsersTab
      ? [
          {
            key: "tier",
            label: tier.title,
            value: tier.activeUsers,
            meta: tier.inviteText ?? tier.activeUsersLabel,
          },
        ]
      : []),
  ];

  const renderRow = (user: ReferredUserRow) => {
    const referralOrPromoCode = getReferralOrPromoCode(
      user.referral_codes,
      USERS_TAB_TO_REFERRAL_TYPE[tab]
    );
    return (
      <>
        <Line>
          <CopyValue
            strong={true}
            onClick={() => {
              copyCodeToClipboard(String(user.customer_id)).catch(() => {});
              openMessage(getCopyMessage(t("users.customer_id")), "info");
            }}
          >
            {user.customer_id}
          </CopyValue>
          <RowFigure>{formatUsd(user.commission)}</RowFigure>
        </Line>
        <Line>
          <RowMeta>{formatDate(user.time, true, true)}</RowMeta>
          <RowSubFigure>
            {t("users.revenue")} {formatUsd(user.revenue)}
          </RowSubFigure>
        </Line>
        <Line>
          <CopyValue
            isLink={isReferredUsersTab}
            small={true}
            onClick={() =>
              copyCodeOrLinkToClipboard(referralOrPromoCode, isReferredUsersTab)
            }
          >
            {referralOrPromoCode}
          </CopyValue>
        </Line>
      </>
    );
  };

  return (
    <TabLayout
      data-testid={`affiliation-${tab}`}
      summary={
        <TabSummary
          loading={loading}
          lead={{
            label: t(countStatistic),
            value: count,
            meta: !loading && (
              <ChangeMeta
                value={countLastMonth}
                formatted={`+${countLastMonth}`}
                suffix={fromLastMonth}
              />
            ),
          }}
          items={items}
        />
      }
      table={
        <TablePanel
          title={tableTitle}
          count={users?.length}
          loading={loading}
          table={<UsersTable tab={tab} referredUsers={users} loading={false} />}
        />
      }
      list={
        <RowList<ReferredUserRow>
          title={tableTitle}
          items={users as ReferredUserRow[] | undefined}
          getKey={(user) => `${user.customer_id}-${user.time}`}
          renderRow={renderRow}
          filter={(user, search) =>
            checkSearch(user.referral_codes.toString(), search)
          }
          loading={loading}
          sortOptions={[
            {
              key: "time",
              label: isReferredUsersTab
                ? t("users.registered_date")
                : t("users.paid_date"),
              compare: (a, b) => sortByNumberProperty(a, b, "time"),
            },
            {
              key: "commission",
              label: t("users.earned"),
              compare: (a, b) => sortByNumberProperty(a, b, "commission"),
            },
            {
              key: "revenue",
              label: t("users.revenue"),
              compare: (a, b) => sortByNumberProperty(a, b, "revenue"),
            },
            {
              key: "customer_id",
              label: t("users.customer_id"),
              compare: (a, b) => sortByNumberProperty(a, b, "customer_id"),
            },
            {
              key: "referral_codes",
              label: isReferredUsersTab
                ? t("users.referral_link")
                : t("users.promo_codes"),
              compare: (a, b) =>
                sortByStringProperty(a, b, "referral_codes[0]"),
            },
          ]}
          empty={<ReferralEmptyState tab={tab} />}
          searchEmpty={<ReferralEmptyState tab={tab} isSearch={true} />}
        />
      }
      // Above the table, like the earnings chart, so the table keeps its
      // full width.
      aside={isReferredUsersTab && <ReferredUsersPanel />}
      asideOnTop={true}
    />
  );
};

export default NewLookUsers;
